import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { doc, serverTimestamp, setDoc } from "firebase/firestore";
import { db } from "../firebase";
import { createApplication } from "../services/applicationsService";
import MarketingNav from "../components/MarketingNav";
import ModalHeader from "../components/ModalHeader";
import { NICHE_OPTIONS } from "../components/niche";
import "./LandingPage.css";
import "./WaitlistCreator.css";

const NICHES = NICHE_OPTIONS.map((option) => option.label);

const POSTAL_RE = /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/;

const WELCOME_PERKS = [
  {
    title: "Canadian creators only",
    body: "All creators on the platform are Canadian. Fans worldwide are welcome.",
    icon: "maple",
  },
  {
    title: "Review takes ~2 minutes",
    body: "A short application — we review every one personally.",
    icon: "clock",
  },
  {
    title: "10% founding fee · no algorithm, ever",
    body: "Founding creators lock in a 10% platform fee permanently. Feeds stay chronological — never ranked.",
    icon: "lock",
  },
];

function PerkIcon({ type }) {
  if (type === "clock") {
    return (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v5l3 2" />
      </svg>
    );
  }
  if (type === "lock") {
    return (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="5" y="11" width="14" height="10" rx="2" />
        <path d="M8 11V8a4 4 0 018 0v3" />
      </svg>
    );
  }
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M12 3c.4 2.8 2.2 4.6 5 5-2.8.4-4.6 2.2-5 5-.4-2.8-2.2-4.6-5-5 2.8-.4 4.6-2.2 5-5z"
        fill="currentColor"
      />
      <path
        d="M12 11c.25 1.7 1.3 2.8 3 3-1.7.25-2.8 1.3-3 3-.25-1.7-1.3-2.8-3-3 1.7-.25 2.8-1.3 3-3z"
        fill="currentColor"
        opacity="0.55"
      />
    </svg>
  );
}

export default function WaitlistCreator() {
  const navigate = useNavigate();
  const [step, setStep] = useState("welcome");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [handle, setHandle] = useState("");
  const [niche, setNiche] = useState("");
  const [contentLink, setContentLink] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [oneSentence, setOneSentence] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const cleanEmail = email.trim().toLowerCase();
    const cleanName = name.trim();
    const cleanHandle = handle.trim().replace(/^@/, "");
    const cleanPostal = postalCode.trim().toUpperCase();

    if (!cleanName || !cleanEmail || !cleanHandle || !niche || !contentLink.trim() || !cleanPostal || !oneSentence.trim()) {
      setError("Please fill out all fields.");
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanEmail)) {
      setError("Please enter a valid email address.");
      return;
    }

    if (!POSTAL_RE.test(cleanPostal)) {
      setError("Please enter a valid Canadian postal code (e.g. N0M 1A0).");
      return;
    }

    try {
      setLoading(true);
      setError("");

      await setDoc(
        doc(db, "waitlist_creators", cleanEmail),
        {
          email: cleanEmail,
          name: cleanName,
          handle: cleanHandle,
          niche,
          contentLink: contentLink.trim(),
          postalCode: cleanPostal,
          oneSentence: oneSentence.trim(),
          approved: false,
          joinedAt: serverTimestamp(),
        },
        { merge: true }
      );

      await createApplication({
        handle: cleanHandle,
        email: cleanEmail,
        postalCode: cleanPostal,
        niche,
        contentLink: contentLink.trim(),
        oneSentence: oneSentence.trim(),
        name: cleanName,
        status: "pending",
        fansReferred: 0,
      });

      setSuccess(true);
    } catch (err) {
      console.error("Creator application error:", err);
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="lp-page">
      <MarketingNav />

      <main className="lp-main lp-form-page">
        <Link to="/welcome" className="lp-back">
          ← Back to home
        </Link>

        {success ? (
          <div className="lp-waitlist-card">
            <div className="lp-form-success">
              <div className="lp-form-success-icon" aria-hidden="true">✦</div>
              <h1 className="lp-form-page-title">Application received.</h1>
              <p>
                We review every application personally. Check your inbox for a confirmation —
                we&apos;ll be in touch within a few days.
              </p>
              <Link to="/" className="lp-btn-primary" style={{ marginTop: "24px", display: "inline-flex" }}>
                Back to home
              </Link>
            </div>
          </div>
        ) : step === "welcome" ? (
          <div className="ca-welcome-card">
            <ModalHeader
              headline={<>Welcome, <em>Creator!</em></>}
              subtext="So glad to have you here. Let's get your application started."
              onClose={() => navigate("/welcome")}
              headingId="ca-welcome-heading"
            />
            <div className="ca-welcome-body">
              <ul className="ca-welcome-perks">
                {WELCOME_PERKS.map((perk) => (
                  <li key={perk.title} className="ca-welcome-perk">
                    <span className="ca-welcome-perk-icon" aria-hidden="true">
                      <PerkIcon type={perk.icon} />
                    </span>
                    <div>
                      <strong>{perk.title}</strong>
                      <span>{perk.body}</span>
                    </div>
                  </li>
                ))}
              </ul>
              <button
                type="button"
                className="lp-btn-primary full"
                onClick={() => setStep("form")}
              >
                Start My Application
              </button>
            </div>
          </div>
        ) : (
          <div className="lp-waitlist-card">
            <p className="lp-form-eyebrow">Creator application</p>
            <h1 className="lp-form-page-title">Apply as a Creator</h1>
            <p className="lp-form-page-sub">
              Canadian creators only. We review every application — not everyone is approved, and that&apos;s intentional.
            </p>

            <form onSubmit={handleSubmit}>
              <div className="lp-field">
                <label htmlFor="creator-name">Your name</label>
                <input
                  id="creator-name"
                  type="text"
                  placeholder="Jade Morrison"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  autoComplete="name"
                  required
                />
              </div>

              <div className="lp-field">
                <label htmlFor="creator-email">Email</label>
                <input
                  id="creator-email"
                  type="email"
                  placeholder="you@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  required
                />
              </div>

              <div className="lp-field">
                <label htmlFor="creator-handle">Preferred handle</label>
                <input
                  id="creator-handle"
                  type="text"
                  placeholder="yourhandle"
                  value={handle}
                  onChange={(e) => setHandle(e.target.value)}
                  required
                />
              </div>

              <div className="lp-field">
                <label htmlFor="creator-niche">Primary niche</label>
                <select
                  id="creator-niche"
                  value={niche}
                  onChange={(e) => setNiche(e.target.value)}
                  required
                >
                  <option value="">Select a niche</option>
                  {NICHES.map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </div>

              <div className="lp-field">
                <label htmlFor="creator-link">Link to your content</label>
                <input
                  id="creator-link"
                  type="url"
                  placeholder="https://instagram.com/you"
                  value={contentLink}
                  onChange={(e) => setContentLink(e.target.value)}
                  required
                />
              </div>

              <div className="lp-field">
                <label htmlFor="creator-postal">Canadian postal code</label>
                <input
                  id="creator-postal"
                  type="text"
                  placeholder="N0M 1A0"
                  value={postalCode}
                  onChange={(e) => setPostalCode(e.target.value)}
                  autoComplete="postal-code"
                  required
                />
              </div>

              <div className="lp-field">
                <label htmlFor="creator-sentence">What do you want to post here?</label>
                <textarea
                  id="creator-sentence"
                  placeholder="One sentence about the content you'd share on 100ViewsOnly…"
                  value={oneSentence}
                  onChange={(e) => setOneSentence(e.target.value)}
                  rows={3}
                  required
                />
              </div>

              {error && <p className="lp-form-error">{error}</p>}

              <button type="submit" className="lp-btn-primary full" disabled={loading}>
                {loading ? "Submitting…" : "Submit application"}
              </button>
            </form>

            <p className="lp-creator-note">
              By applying, you agree to receive application updates. Unsubscribe anytime.
            </p>
          </div>
        )}
      </main>
    </div>
  );
}
